/**
 * Phase 18 — Voice & Vernacular Commerce Engine
 * Voice search (Web Speech API), accessibility controls,
 * language card interactions, coverage bar fills, scroll reveals.
 */
(function () {
  'use strict';

  /* ── Scroll Reveal ── */
  function initScrollReveal() {
    var els = document.querySelectorAll(
      '.lang-sel__card, .lang-sel__coverage-item, .lang-sel__seo-card, ' +
      '.voice-com__cmd-card, .voice-com__platform-card, ' +
      '.a11y__cat-card, .a11y__feature-card, .a11y__control-item'
    );
    if (!els.length) return;

    els.forEach(function (el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(16px)';
      el.style.transition = 'opacity .5s ease, transform .5s ease';
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var idx = Array.from(entry.target.parentElement.children).indexOf(entry.target);
          setTimeout(function () {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }, idx * 70);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ── Coverage Bar Fills ── */
  function initCoverageBars() {
    var bars = document.querySelectorAll('.lang-sel__coverage-fill');
    if (!bars.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var w = entry.target.getAttribute('data-width');
          if (w) entry.target.style.width = w + '%';
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    bars.forEach(function (b) { io.observe(b); });
  }

  /* ── Language Card Selection ── */
  function initLanguageCards() {
    var cards = document.querySelectorAll('.lang-sel__card');
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        cards.forEach(function (c) { c.classList.remove('lang-sel__card-active'); });
        card.classList.add('lang-sel__card-active');
      });
    });
  }

  /* ── Voice Search (Web Speech API) ── */
  function initVoiceSearch() {
    var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    var micTrigger = document.getElementById('voice-mic-trigger');
    var searchBtn = document.getElementById('voice-search-btn');
    var searchInput = document.getElementById('voice-search-input');
    var transcript = document.getElementById('voice-transcript');

    if (!SpeechRecognition || !searchInput) return;

    var recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';

    var listening = false;

    function startListening() {
      if (listening) return;
      try {
        recognition.start();
        listening = true;
        if (transcript) {
          transcript.textContent = 'Listening...';
          transcript.classList.add('voice-com__transcript--active');
        }
        if (micTrigger) {
          var circle = micTrigger.querySelector('.voice-com__mic-circle');
          if (circle) circle.style.borderColor = '#C8A74C';
        }
      } catch (e) {
        // already started
      }
    }

    function stopListening() {
      listening = false;
      try { recognition.stop(); } catch (e) { /* noop */ }
      if (micTrigger) {
        var circle = micTrigger.querySelector('.voice-com__mic-circle');
        if (circle) circle.style.borderColor = '';
      }
    }

    recognition.onresult = function (event) {
      var text = '';
      for (var i = event.resultIndex; i < event.results.length; i++) {
        text += event.results[i][0].transcript;
      }
      if (searchInput) searchInput.value = text;
      if (transcript) transcript.textContent = '"' + text + '"';
    };

    recognition.onend = function () {
      listening = false;
      if (micTrigger) {
        var circle = micTrigger.querySelector('.voice-com__mic-circle');
        if (circle) circle.style.borderColor = '';
      }
      // Auto-search after voice input
      if (searchInput && searchInput.value.trim()) {
        var val = searchInput.value.trim().toLowerCase();
        if (val.includes('cart') || val.includes('add')) {
          if (transcript) transcript.textContent += ' → Opening cart...';
        } else if (val.includes('order') || val.includes('track')) {
          if (transcript) transcript.textContent += ' → Checking your orders...';
        } else {
          if (transcript) transcript.textContent += ' → Searching products...';
          setTimeout(function () {
            window.location.href = '/search?q=' + encodeURIComponent(searchInput.value.trim());
          }, 1200);
        }
      }
    };

    recognition.onerror = function () {
      listening = false;
      if (transcript) {
        transcript.textContent = 'Could not hear you. Please try again.';
      }
    };

    if (micTrigger) {
      micTrigger.addEventListener('click', function () {
        if (listening) stopListening();
        else startListening();
      });
      micTrigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (listening) stopListening();
          else startListening();
        }
      });
    }

    if (searchBtn) {
      searchBtn.addEventListener('click', function () {
        if (listening) stopListening();
        else startListening();
      });
    }
  }

  /* ── Accessibility Controls ── */
  function initA11yControls() {
    // High Contrast
    var contrastToggle = document.getElementById('a11y-contrast');
    if (contrastToggle) {
      var savedContrast = localStorage.getItem('shiffa-a11y-contrast');
      if (savedContrast === 'true') {
        contrastToggle.checked = true;
        document.documentElement.classList.add('a11y-high-contrast');
      }
      contrastToggle.addEventListener('change', function () {
        document.documentElement.classList.toggle('a11y-high-contrast', this.checked);
        localStorage.setItem('shiffa-a11y-contrast', this.checked);
      });
    }

    // Reduced Motion
    var motionToggle = document.getElementById('a11y-motion');
    if (motionToggle) {
      var savedMotion = localStorage.getItem('shiffa-a11y-motion');
      if (savedMotion === 'true') {
        motionToggle.checked = true;
        document.documentElement.classList.add('a11y-reduced-motion');
      }
      motionToggle.addEventListener('change', function () {
        document.documentElement.classList.toggle('a11y-reduced-motion', this.checked);
        localStorage.setItem('shiffa-a11y-motion', this.checked);
      });
    }

    // Color Blind
    var cbToggle = document.getElementById('a11y-colorblind');
    if (cbToggle) {
      var savedCB = localStorage.getItem('shiffa-a11y-colorblind');
      if (savedCB === 'true') {
        cbToggle.checked = true;
        document.documentElement.classList.add('a11y-colorblind');
      }
      cbToggle.addEventListener('change', function () {
        document.documentElement.classList.toggle('a11y-colorblind', this.checked);
        localStorage.setItem('shiffa-a11y-colorblind', this.checked);
      });
    }

    // Font Size
    var fontBtns = document.querySelectorAll('.a11y__font-btn');
    if (fontBtns.length) {
      var savedFont = localStorage.getItem('shiffa-a11y-fontsize') || 'normal';
      applyFontSize(savedFont);
      fontBtns.forEach(function (btn) {
        if (btn.getAttribute('data-fontsize') === savedFont) {
          btn.classList.add('a11y__font-btn--active');
        } else {
          btn.classList.remove('a11y__font-btn--active');
        }
      });

      fontBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var size = btn.getAttribute('data-fontsize');
          fontBtns.forEach(function (b) { b.classList.remove('a11y__font-btn--active'); });
          btn.classList.add('a11y__font-btn--active');
          applyFontSize(size);
          localStorage.setItem('shiffa-a11y-fontsize', size);
        });
      });
    }

    function applyFontSize(size) {
      var root = document.documentElement;
      root.classList.remove('a11y-font-normal', 'a11y-font-large', 'a11y-font-xlarge');
      root.classList.add('a11y-font-' + size);
    }
  }

  /* ── Init ── */
  function init() {
    initScrollReveal();
    initCoverageBars();
    initLanguageCards();
    initVoiceSearch();
    initA11yControls();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
